import { validate as uuidValidate, version as uuidVersion } from "uuid";

/** Values commonly coming from Vue Router params or query. */
export type UuidRouteParam = string | string[] | null | undefined;

export type UuidVersion = 1 | 2 | 3 | 4 | 5;

export interface IsValidUuidOptions {
  /**
   * When true (default), surrounding whitespace is removed before validation.
   */
  trim?: boolean;
  /**
   * When set, the UUID must be this RFC version (1–5).
   * When omitted, any syntactically valid UUID is accepted.
   */
  version?: UuidVersion;
}

const normalizeScalarToString = (value: unknown, trim: boolean): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string") {
    const s = trim ? value.trim() : value;
    return s === "" ? undefined : s;
  }
  return undefined;
};

/**
 * Normalizes route/query-style input to a single string or undefined.
 * - `string` → that string (trimmed if `trim` is true)
 * - single-element `string[]` → that element
 * - empty array, multi-element array, or non-string → undefined
 */
export const normalizeUuidRouteParam = (value: unknown, trim = true): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    if (value.length !== 1) {
      return undefined;
    }
    return normalizeScalarToString(value[0], trim);
  }
  return normalizeScalarToString(value, trim);
};

/**
 * Validates a string as a UUID (after optional trim).
 * Use this when the value is already a single string.
 */
export const isValidUuidString = (value: string, options?: IsValidUuidOptions): boolean => {
  const trim = options?.trim !== false;
  const s = trim ? value.trim() : value;
  if (!s) {
    return false;
  }
  if (!uuidValidate(s)) {
    return false;
  }
  if (options?.version !== undefined && uuidVersion(s) !== options.version) {
    return false;
  }
  return true;
};

/**
 * Generalized UUID check for strings and common router inputs.
 *
 * - `null` / `undefined` → false
 * - `string` → validated as UUID
 * - single-element `string[]` → validates that element only
 * - multi-element array, objects, numbers, etc. → false
 *
 * @param value - Raw value (e.g. route param, query, or plain string)
 * @param options - Optional trim (default true) and RFC version filter
 */
export const isValidUuid = (value: unknown, options?: IsValidUuidOptions): boolean => {
  const trim = options?.trim !== false;
  const normalized = normalizeUuidRouteParam(value, trim);
  if (normalized === undefined) {
    return false;
  }
  return isValidUuidString(normalized, { ...options, trim: false });
};
